import { useState } from "react";
import { useAlbum } from "./data/useManifest";
import { MobileReader } from "./reader/MobileReader";
import { DesktopReader } from "./reader/DesktopReader";
import { CheckoutBanner } from "./reader/CheckoutBanner";
import { GatePage, GateDeadCard } from "./reader/GatePage";
import { GoDarkPage } from "./reader/GoDarkPage";
import { LiveUpdate } from "./reader/LiveUpdate";
import { useMediaQuery, DESKTOP_QUERY } from "./reader/useMediaQuery";
import { ParityGrid } from "./dev/ParityGrid";
import "./reader/components.css";
import "./App.css";

import { hideEmptyPages, type AlbumManifest } from "./data/manifest";

const params = new URLSearchParams(window.location.search);
const PARITY = params.has("parity");
const FORCE_DESKTOP = params.has("desktop");
const FORCE_MOBILE = params.has("mobile");
// Dev: ?cover=color renders Anvil's color+plate cover example (validates the
// baked-title plate path). No effect in normal use.
const COVER_COLOR = params.get("cover") === "color";
// Dev: ?buyable forces print.available so the enabled full-strength CTA is
// visible before Anvil's slice-2 flips the real manifest. No effect otherwise.
const FORCE_BUYABLE = params.has("buyable");
// Dev: ?godark previews the §25.5 "album no longer shared" page (the branded
// surface Anvil's 410 not_shared resolves to). No effect on a real load.
const FORCE_GODARK = params.has("godark");

function withColorCover(m: AlbumManifest): AlbumManifest {
  return {
    ...m,
    cover: {
      background: { type: "color", colorId: "ink" },
      title: {
        text: "Belek Sommaren",
        fontId: "fraunces",
        weight: 600,
        sizePt: 40,
        position: { x: 0.5, y: 0.82 },
        align: "center",
        color: "auto",
        shadow: false,
        plate: { enabled: true, colorId: "ink" },
      },
      showTitle: true,
    },
    coverLook: "none",
  };
}

/**
 * Reader entry. Mobile-first per the build sequence: the responsive column
 * below drives phone + tablet today; the desktop two-page SPREAD with
 * page-flip is the next task and will branch here at >=1280.
 */
export default function App() {
  const { state, unlock, hasUpdate, applyUpdate, dismissUpdate } = useAlbum();
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  // Whether the viewer is still on the cover (hasn't started reading) — the
  // readers report it up so the live-update overlay can auto-refresh on the
  // cover vs. offer the pill mid-read. Mounted once here so a phase change in
  // the reader never remounts / re-triggers it.
  const [atCover, setAtCover] = useState(true);
  const liveEl = (
    <LiveUpdate
      hasUpdate={hasUpdate}
      atCover={atCover}
      onApply={applyUpdate}
      onDismiss={dismissUpdate}
    />
  );

  // Dev preview of the §25.5 go-dark page (all hooks above run first).
  if (FORCE_GODARK) return <GoDarkPage />;

  if (state.status === "loading") {
    return <Centered>Opening the album…</Centered>;
  }
  if (state.status === "gated") {
    // Access gate: the reader loads only after the code is entered.
    return <GatePage gate={state.gate} onUnlock={unlock} />;
  }
  if (state.status === "revoked") {
    // 410 not_shared — sharing turned off, or the owner's Backup & Share lapsed.
    // §25.5: a warm branded page, not a dead card (recipient holds this link).
    return <GoDarkPage />;
  }
  if (state.status === "notfound") {
    return <GateDeadCard kind="notfound" />;
  }
  if (state.status === "error") {
    return <Centered>Something went wrong opening the album.</Centered>;
  }

  let manifest = COVER_COLOR ? withColorCover(state.manifest) : state.manifest;
  if (FORCE_BUYABLE) {
    manifest = { ...manifest, print: { available: true, checkoutUrl: null } };
  }

  if (PARITY) return <ParityGrid manifest={manifest} />;

  // Hide blank padding pages (print's 24pp Prodigi minimum) for a clean web read
  // — display-only, after the parity harness which wants the full print book.
  manifest = hideEmptyPages(manifest);

  const desktop = FORCE_MOBILE ? false : FORCE_DESKTOP || isDesktop;
  if (desktop) {
    return (
      <>
        <CheckoutBanner />
        {liveEl}
        <DesktopReader manifest={manifest} onCoverChange={setAtCover} />
      </>
    );
  }

  return (
    <>
      <CheckoutBanner />
      {liveEl}
      <div className="app-frame">
        <MobileReader manifest={manifest} onCoverChange={setAtCover} />
      </div>
    </>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="app-centered">
      <p>{children}</p>
    </div>
  );
}
